/** Shown by the browser's own prompt where it allows custom text, and by the in-app confirm. */
export const RECORDING_LEAVE_MESSAGE = 'Recording in progress. End recording before leaving.';

interface GuardEvent {
  preventDefault(): void;
  returnValue?: unknown;
}

interface ClickEvent extends GuardEvent {
  defaultPrevented?: boolean;
  button?: number;
  metaKey?: boolean;
  ctrlKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  target: unknown;
  stopPropagation(): void;
}

interface AnchorLike {
  closest?: (selector: string) => AnchorLike | null;
  getAttribute?: (name: string) => string | null;
  hasAttribute?: (name: string) => boolean;
  target?: string;
}

export interface RecordingLeaveGuardEnv {
  window: {
    addEventListener(type: 'beforeunload', listener: (event: GuardEvent) => void): void;
    removeEventListener(type: 'beforeunload', listener: (event: GuardEvent) => void): void;
    confirm(message: string): boolean;
    location: { href: string; origin: string };
  };
  document: {
    addEventListener(type: 'click', listener: (event: ClickEvent) => void, capture: boolean): void;
    removeEventListener(type: 'click', listener: (event: ClickEvent) => void, capture: boolean): void;
  };
}

/** The internal link a plain left click would follow to another page, or undefined. */
function leavingAnchor(event: ClickEvent, env: RecordingLeaveGuardEnv): AnchorLike | undefined {
  if (event.defaultPrevented || (event.button ?? 0) !== 0) return undefined;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return undefined;
  const target = event.target as AnchorLike | null;
  const anchor = target?.closest?.('a[href]') ?? undefined;
  // Only an HTML anchor is a page navigation here. An SVG <a> (whose `href` and `target` are
  // SVGAnimatedString objects) and anything else is skipped.
  // `typeof HTMLAnchorElement === 'undefined'`: non-DOM test environments only; browsers always define it.
  if (!anchor || typeof HTMLAnchorElement === 'undefined' || !(anchor instanceof HTMLAnchorElement)) return undefined;
  if (anchor.target && anchor.target !== '_self') return undefined;
  if (anchor.hasAttribute?.('download')) return undefined;
  const href = anchor.getAttribute?.('href');
  if (!href) return undefined;
  try {
    const next = new URL(href, env.window.location.href);
    const here = new URL(env.window.location.href);
    if (next.origin !== here.origin) return undefined;
    return next.pathname === here.pathname && next.search === here.search ? undefined : anchor;
  } catch {
    return undefined;
  }
}

/**
 * While an in-person recording is running: warns before the tab is closed or reloaded
 * (`beforeunload`, as `InPersonRecordingPage` does) and asks before an in-app link leaves the page.
 * The app runs a plain `BrowserRouter` (no data router), so react-router's `useBlocker` is unavailable;
 * a capture-phase click confirm on internal links stands in for it. Browser Back/Forward inside the app
 * cannot be intercepted under that router. Returns the cleanup that removes both listeners.
 */
export function installRecordingLeaveGuard(env: RecordingLeaveGuardEnv): () => void {
  const warn = (event: GuardEvent): void => {
    event.preventDefault();
    event.returnValue = RECORDING_LEAVE_MESSAGE;
  };
  const onClick = (event: ClickEvent): void => {
    if (!leavingAnchor(event, env)) return;
    if (env.window.confirm(RECORDING_LEAVE_MESSAGE)) return;
    event.preventDefault();
    event.stopPropagation();
  };
  env.window.addEventListener('beforeunload', warn);
  env.document.addEventListener('click', onClick, true);
  return () => {
    env.window.removeEventListener('beforeunload', warn);
    env.document.removeEventListener('click', onClick, true);
  };
}
